import { useMemo } from "react"
import { DashBoardConditions } from "features/dashBoard/types"
import { filter } from "../../common"
import { useHeatMapGraphOption, TimeOperations } from "./options"
import { DashBoardValue } from "features/dashBoard/hooks"
import { DownLoadData, DownLoadHeader, DownLoadRecord } from "components/download/csv/types"

type useHeatMapGraphModelProps<T> = {
  dashBoard: DashBoardValue<T>
	dataList: T[] | null
	conditions: DashBoardConditions,
	graphOption: ReturnType<typeof useHeatMapGraphOption<T>>
}
export const useHeatMapGraphModel = <T,>({
	dashBoard: {
		subSource: {
			lv: { dataState: lvLogListState },
			mrp: { dataState: mrpLogListState }
		}
	},
	dataList,
	conditions,
	graphOption: {
		configure, targetList,
    timeDef, by, valueDef, total
  }
}: useHeatMapGraphModelProps<T>) => useMemo(
	() => {
		if (!timeDef?.key || !timeDef.time) return null

		const operation = TimeOperations[by]
		if (!operation) return null

		const condition = conditions[timeDef.key]

		let maxValue: number | null = null
		let minTime: Date | null = null
		let maxTime: Date | null = null
		const values: number[][] = []
		if (total) {
			if (timeDef.source === 'mrp') {
				const byMrpLog = timeDef.byMrpLog
				if (byMrpLog) {
					const logs = mrpLogListState.data?.list || []
					for(const log of logs) {
						const time = byMrpLog(log)
						if (!time) continue

						const { x, y } = operation.value(time)
						let it = values.find(([vx, vy, _]) => vx === x && vy === y)
						if (!it) {
							it = [x, y, 1]
							values.push(it)
						} else {
							it[2] = it[2] + 1
						}
						if (maxValue === null || maxValue < it[2]) maxValue = it[2]
						if (minTime === null || minTime.getTime() > time.getTime()) minTime = time
						if (maxTime === null || maxTime.getTime() < time.getTime()) maxTime = time			
					}
				}
			} else if (timeDef.source === 'lv') {
				const byLvLog = timeDef.byLvLog
				if (byLvLog) {
					const logs = lvLogListState.data?.list || []
					for(const log of logs) {
						const time = byLvLog(log)
						if (!time) continue

						const { x, y } = operation.value(time)
						let it = values.find(([vx, vy, _]) => vx === x && vy === y)
						if (!it) {
							it = [x, y, 1]
							values.push(it)
						} else {
							it[2] = it[2] + 1
						}
						if (maxValue === null || maxValue < it[2]) maxValue = it[2]
						if (minTime === null || minTime.getTime() > time.getTime()) minTime = time
						if (maxTime === null || maxTime.getTime() < time.getTime()) maxTime = time			
					}
				}
			}
		} else {
			if (!dataList || dataList.length === 0) return null
			if (!valueDef?.key) return null

			for (const record of dataList) {
				const time = timeDef.time(record)
				if (!time) continue

				const value = valueDef.val(record)
				if (value === undefined || value === 0) return
				if (!filter(record, configure, conditions, (condition && condition.time) ? timeDef.key : undefined, targetList)) continue

				const timeList: Date[] = Array.isArray(time) ? time : [time]
				for (const time of timeList) {
					const { x, y } = operation.value(time)
					let it = values.find(([vx, vy, _]) => vx === x && vy === y)
					if (!it) {
						it = [x, y, value]
						values.push(it)
					} else {
						it[2] = it[2] + value
					}
					if (maxValue === null || maxValue < it[2]) maxValue = it[2]
					if (minTime === null || minTime.getTime() > time.getTime()) minTime = time
					if (maxTime === null || maxTime.getTime() < time.getTime()) maxTime = time			
				}
			}
		}
		const axisLabels = (minTime !== null && maxTime !== null) ? operation.axisLabels(minTime, maxTime) : { x: [], y: [] }
		return {
			operation,
			maxValue,
			axisLabels,
			values,
			csvBuilder: (): DownLoadData | undefined => {
				if (!timeDef?.key || !valueDef?.key) return undefined

				const unit = valueDef.unit
				const { x: xLabels, y: yLabels } = axisLabels

				const header: DownLoadHeader[] = xLabels.map(
					(label, x) => ({ title: label, key: `col${x}`, unit })
				)
				header.unshift({ title: "-", key: "y" })

				const body: DownLoadRecord[] = yLabels.map((label, y) => {
					const row: { [key: string]: string | number } = {}
					row["y"] = label
					xLabels.forEach(
						(_, x) => {
							const value = values.find(([vx, vy, _]) => vx === x && vy === y)
							if (value) row[`col${x}`] = value[2]
						}
					)
					return row
				})
				return { header, body }
			}
		}
	},
	[
		dataList, configure, conditions, targetList,
		timeDef, by, valueDef, total,
		lvLogListState, mrpLogListState
	]
)
